/**
 * @fileoverview Welcome step for onboarding - displays branding and value proposition.
 * @module features/onboarding/components/steps/WelcomeStep
 */

import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollReveal } from '@/shared/components/ui'

interface WelcomeStepProps {
  /** Callback when user clicks Get Started */
  onContinue: () => void
}

/**
 * First step of onboarding wizard.
 * Shows AbyssBlock logo, welcome message, and value proposition.
 *
 * @component
 * @example
 * ```tsx
 * <WelcomeStep onContinue={() => goToStep('create-org')} />
 * ```
 */
export function WelcomeStep({ onContinue }: WelcomeStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')

  // Listen for Enter key to continue
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') {
        onContinue()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onContinue])

  return (
    <div className="flex flex-col items-center justify-center max-w-2xl mx-auto text-center">
      {/* Logo - flies in from large scale */}
      <motion.div
        initial={{
          opacity: 0,
          scale: 3,
          filter: 'blur(24px)',
        }}
        animate={{
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)',
        }}
        transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1],
        }}
      >
        <img
          src="/ab-black-white-pill-logo.webp"
          alt="AbyssBlock"
          className="h-36 w-auto mb-8"
        />
      </motion.div>

      {/* Welcome heading */}
      <ScrollReveal animateOnMount blur delay={0.1}>
        <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
          {t('welcome.title')}
        </h1>
      </ScrollReveal>

      {/* Value proposition */}
      <ScrollReveal animateOnMount blur delay={0.2}>
        <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
          {t('welcome.subtitle')}
        </p>
      </ScrollReveal>

      {/* CTA button */}
      <ScrollReveal animateOnMount blur delay={0.3}>
        <Button variant="outline" size="lg" onClick={onContinue}>
          {t('welcome.cta')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </ScrollReveal>
    </div>
  )
}
